import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Building2, ArrowRight, Shield } from 'lucide-react';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1'}/user/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          lang: localStorage.getItem('i18nextLng') || 'ar'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(t('auth.otpSent'));
        setTimeout(() => {
          setStep(2);
          setError('');
        }, 1000);
      } else {
        setError(data.message || t('auth.errorOccurred'));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(t('auth.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1'}/user/verify-reset-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp,
          lang: localStorage.getItem('i18nextLng') || 'ar'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setResetToken(data.data.resetToken);
        setSuccess(t('auth.otpVerified'));
        setTimeout(() => {
          setStep(3);
          setError('');
        }, 1000);
      } else {
        setError(data.message || t('auth.errorOccurred'));
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(t('auth.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://back.miskest.com/api/v1'}/user/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          resetToken,
          newPassword,
          lang: localStorage.getItem('i18nextLng') || 'ar'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(t('auth.passwordResetSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(data.message || t('auth.errorOccurred'));
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t('auth.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="bg-pattern"></div>
        <div className="bg-overlay"></div>
      </div>
      
      <div className="login-content">
        <div className="login-left">
          <div className="brand-section">
            <div className="brand-logo">
              <Building2 size={48} />
            </div>
            <h1>{t('common.appFullName')}</h1>
            <p>منصتك الذكية لإدارة العروض والطلبات العقارية</p>
          </div>
          
          <div className="features-list">
            <div className="feature-item">
              <div className="feature-icon">
                <Shield size={24} />
              </div>
              <div className="feature-text">
                <h3>إعادة تعيين آمنة</h3>
                <p>نظام آمن ومشفر لإعادة تعيين كلمة المرور</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <Mail size={24} />
              </div>
              <div className="feature-text">
                <h3>تحقق بالبريد</h3>
                <p>رمز تحقق يُرسل مباشرة لبريدك الإلكتروني</p>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <ArrowRight size={24} />
              </div>
              <div className="feature-text">
                <h3>خطوات بسيطة</h3>
                <p>3 خطوات فقط لاستعادة حسابك</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="login-right">
          <div className="login-card">
            <div className="login-header">
              <h2>{t('auth.forgotPassword')}</h2>
              <p className="step-indicator">
                {t('auth.step')} {step} {t('auth.of')} 3
              </p>
            </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            {success}
            {step === 2 && (
              <div className="spam-notice">
                <span className="spam-icon">📧</span>
                {t('auth.checkSpamFolder', 'إذا لم تجد الرسالة، يرجى التحقق من مجلد البريد غير الهام (Spam)')}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleRequestOTP} className="login-form">
            <p className="form-description">{t('auth.enterEmailForReset')}</p>
            
            <div className="form-group">
              <label htmlFor="email">
                <Mail size={18} />
                {t('auth.email')}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner"></div>
                  {t('auth.sending')}
                </>
              ) : (
                t('auth.sendOTP')
              )}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyOTP} className="login-form">
            <p className="form-description">{t('auth.enterOTPSent')}</p>
            
            <div className="form-group">
              <label htmlFor="otp">
                <Shield size={18} />
                {t('auth.verificationCode')}
              </label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="000000"
                maxLength="6"
                required
                disabled={loading}
                className="form-control otp-input"
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner"></div>
                  {t('auth.verifying')}
                </>
              ) : (
                t('auth.verifyOTP')
              )}
            </button>

            <button
              type="button"
              className="btn-text"
              onClick={() => {
                setStep(1);
                setOtp('');
                setError('');
                setSuccess('');
              }}
              disabled={loading}
            >
              {t('auth.backToEmail')}
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleResetPassword} className="login-form">
            <p className="form-description">{t('auth.enterNewPassword')}</p>
            
            <div className="form-group">
              <label htmlFor="newPassword">
                <Lock size={18} />
                {t('auth.newPassword')}
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                className="form-control"
                minLength="6"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                <Lock size={18} />
                {t('auth.confirmPassword')}
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                className="form-control"
                minLength="6"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner"></div>
                  {t('auth.resetting')}
                </>
              ) : (
                t('auth.resetPassword')
              )}
            </button>
          </form>
        )}

            <div className="register-link">
              <Link to="/login" className="back-to-login">
                ← {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
